import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  adminDisable2FA,
  forceLogout,
  getAccount,
  unlockAccount,
  type AccountDetail,
} from "../api/admin";

export function AccountDetailPage() {
  const { api } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<AccountDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    if (!id) return;
    try { setDetail(await getAccount(api, id)); }
    catch (e) { setError(e instanceof Error ? e.message : "load failed"); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  async function run(fn: () => Promise<unknown>, label: string) {
    if (!confirm(`${label} — proceed?`)) return;
    setBusy(true); setMsg(null); setError(null);
    try {
      await fn();
      setMsg(`${label}: success`);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "action failed");
    } finally { setBusy(false); }
  }

  if (error) return <div className="error">{error}</div>;
  if (!detail) return <p className="meta">Loading…</p>;
  const a = detail.account;

  return (
    <div className="vstack">
      <div>
        <h1>{a.email}</h1>
        <p className="meta">
          {a.id} · {a.role} · {a.acct_kind}
          {a.deleted_at && " · DELETED"}
          {a.locked_until && ` · LOCKED until ${a.locked_until}`}
        </p>
      </div>

      <div className="card">
        <h2>Admin actions</h2>
        {msg && <div className="ok">{msg}</div>}
        <div className="hstack" style={{ flexWrap: "wrap", gap: 20 }}>
          {id && a.locked_until && (
            <button className="btn" onClick={() => run(() => unlockAccount(api, id), "Unlock account")} disabled={busy}>
              Unlock
            </button>
          )}
          {id && (
            <button className="btn danger" onClick={() => run(() => forceLogout(api, id), "Force-logout all sessions")} disabled={busy}>
              Force logout everywhere
            </button>
          )}
          {id && a.totp_enabled_at && (
            <button className="btn danger" onClick={() => run(() => adminDisable2FA(api, id), "Disable 2FA")} disabled={busy}>
              Emergency disable 2FA
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <h2>Snapshot</h2>
        <div className="row"><div className="row-title">Active refresh sessions</div><div className="row-meta">{detail.active_sessions}</div></div>
        <div className="row"><div className="row-title">Devices</div><div className="row-meta">{detail.devices.length}</div></div>
        <div className="row"><div className="row-title">Paired installs</div><div className="row-meta">{detail.servers.length}</div></div>
        <div className="row"><div className="row-title">2FA</div><div className="row-meta">{a.totp_enabled_at ?? "not enabled"}</div></div>
        <div className="row"><div className="row-title">Failed logins</div><div className="row-meta">{a.failed_login_count}</div></div>
        <div className="row"><div className="row-title">Password algo</div><div className="row-meta">{a.password_algo} · updated {a.password_updated_at.slice(0, 10)}</div></div>
      </div>

      <div className="card">
        <h2>Devices</h2>
        {detail.devices.length === 0 && <p className="meta">None.</p>}
        {detail.devices.map((d) => (
          <div className="row" key={d.id}>
            <div>
              <div className="row-title">{d.label}{d.revoked_at && <span className="subtle"> · revoked</span>}</div>
              <div className="row-meta">{d.platform} · {d.last_ip ?? "no IP"} · last seen {d.last_seen_at}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <h2>Installs</h2>
        {detail.servers.length === 0 && <p className="meta">None.</p>}
        {detail.servers.map((s) => (
          <div className="row" key={s.id}>
            <div>
              <div className="row-title">{s.label}{s.revoked_at && <span className="subtle"> · revoked</span>}</div>
              <div className="row-meta">{s.server_id} · paired {s.paired_at ?? "—"}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <h2>Recent events</h2>
        {detail.recent_events.length === 0 && <p className="meta">None.</p>}
        {detail.recent_events.map((e) => (
          <div className="row" key={e.id}>
            <div>
              <div className="row-title">{e.event_type}</div>
              <div className="row-meta">{e.ts}{e.ip ? ` · ${e.ip}` : ""}</div>
              {e.detail && Object.keys(e.detail).length > 0 && (
                <div className="row-meta subtle">{JSON.stringify(e.detail)}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
